export const METRIKA_COUNTER_ID = 113658321
const SCRIPT_URL = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_COUNTER_ID}`
const SITE_ORIGIN = 'https://lupaapp.ru'
const PUBLIC_PATHS = new Set(['/', '/landing', '/otslezhivanie-bez-gps', '/for-logists', '/ot-voditelya'])
const ROUTES = new Set([
  ...PUBLIC_PATHS, '/register', '/email-confirmed', '/forgot-password',
  '/password-reset-complete', '/email-change-complete', '/tracking', '/profile', '/saved-links',
  '/manager', '/manager/orders', '/manager/calendar', '/manager/history', '/manager/pricing',
  '/manager/analytics', '/manager/route-planning', '/driver', '/driver/trips', '/driver/documents',
  '/driver/history', '/driver/accept', '/internal/driver-funnel', '/internal/growth',
  '/legal/privacy', '/legal/consent', '/legal/terms', '/404',
])
const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'yclid', 'ysclid']

type Ym = ((id: number, method: string, ...args: unknown[]) => void) & {
  a?: unknown[][]
  l?: number
}
declare global {
  interface Window { ym?: Ym }
}

/** Only marketing pages contain content suitable for session replay. '/' can also be a cabinet. */
export function isMetrikaPublicPage(pathname: string, authenticated: boolean) {
  return !authenticated && PUBLIC_PATHS.has(pathname)
}

/** Allow known paths and campaign tags; never send share IDs, invites, OAuth data or hashes. */
export function metrikaUrl(href: string): string {
  const input = new URL(href, SITE_ORIGIN)
  const pathname = input.pathname.startsWith('/track/') ? '/track/shared'
    : ROUTES.has(input.pathname) ? input.pathname : '/404'
  const output = new URL(pathname, SITE_ORIGIN)
  if (PUBLIC_PATHS.has(pathname)) {
    for (const name of CAMPAIGN_PARAMS) {
      const value = input.searchParams.get(name)
      if (value) output.searchParams.set(name, value.slice(0, 200))
    }
  }
  return output.href
}

function safeReferrer(value: string) {
  if (!value) return ''
  try {
    const url = new URL(value)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return ''
    // External query strings and paths can also contain personal data (including OAuth replies).
    return url.origin === SITE_ORIGIN ? metrikaUrl(value) : `${url.origin}/`
  } catch {
    return ''
  }
}

let mode: 'public' | 'private' | null = null
let lastNavigation = ''
let previousUrl = ''

function loadScript(): Ym {
  if (!window.ym) {
    const queued: Ym = (...args) => { (queued.a ??= []).push(args) }
    queued.l = Date.now()
    window.ym = queued
  }
  if (!Array.from(document.scripts).some((script) => script.src === SCRIPT_URL)) {
    const script = document.createElement('script')
    script.async = true
    script.src = SCRIPT_URL
    // A strict referrer policy also applies to the script request itself.
    script.referrerPolicy = 'strict-origin'
    document.head.appendChild(script)
  }
  return window.ym
}

export function stopMetrika() {
  if (mode) window.ym?.(METRIKA_COUNTER_ID, 'destruct')
  mode = null
  lastNavigation = ''
  previousUrl = ''
}

/** Called from the router, including the first view. All other automatic views are disabled. */
export function trackMetrikaPage(href: string, navigationKey: string, publicPage: boolean) {
  // Local work, previews and automated tests must not pollute production statistics.
  if (!import.meta.env.PROD || window.location.hostname !== 'lupaapp.ru') return
  const url = metrikaUrl(href)
  const nextMode = publicPage ? 'public' : 'private'
  const ym = loadScript()
  const referer = previousUrl || safeReferrer(document.referrer)
  if (mode !== nextMode) {
    if (mode) ym(METRIKA_COUNTER_ID, 'destruct')
    ym(METRIKA_COUNTER_ID, 'init', {
      defer: true,
      ssr: true,
      webvisor: publicPage,
      clickmap: publicPage,
      // Automatically tracking outgoing links can expose invite tokens, file URLs and OAuth URLs.
      trackLinks: false,
      accurateTrackBounce: true,
      ecommerce: 'dataLayer',
      sendTitle: false,
      url,
      referrer: referer,
    })
    mode = nextMode
  }
  const key = `${navigationKey}|${url}|${nextMode}`
  if (key === lastNavigation) return // React StrictMode replays effects.
  ym(METRIKA_COUNTER_ID, 'hit', url, { referer, title: 'Лупа' })
  lastNavigation = key
  previousUrl = url
}
